import type { AdminFoodRequest } from '@steado/admin-contract/foods';
import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { CheckIcon } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/auth-context';
import { cn } from '@/lib/utils';

import { approveNames, queueQueryOptions, requestQueryOptions } from './food-requests-query';
import type { QueueNotice } from './FoodRequestsScreen';
import { ORIGIN_LABELS, SOURCE_NAMES, plural } from './request-copy';
import { ADMIN_ONLY } from './review-parts';

const GRID =
  'grid grid-cols-[44px_minmax(150px,1fr)_minmax(160px,1fr)_minmax(220px,1.6fr)_110px_150px] items-center';
const HEADER_CELL = 'px-3 text-[11px] font-semibold tracking-[0.055em] text-muted-foreground uppercase';

/**
 * The names ready to approve, selected together: high confidence and no
 * other food answering to the word. Adding them writes one History batch
 * each, so any one can be reverted alone; the banner's Undo takes all of them
 * back.
 */
export function BulkNames({
  onBack,
  onOpen,
  onAdded,
  notice,
}: {
  readonly onBack: () => void;
  readonly onOpen: (id: string) => void;
  readonly onAdded: (notice: QueueNotice) => void;
  readonly notice: QueueNotice | null;
}) {
  const { api, can } = useAuth();
  const list = useQuery(queueQueryOptions(api, { status: 'open', kind: 'name', confidence: 'high' }));
  const ready = useMemo(() => (list.data ?? []).filter((request) => request.readyToApprove), [list.data]);
  const details = useQueries({ queries: ready.map((request) => requestQueryOptions(api, request.id)) });
  // Everything ready starts selected, which is what "Select all" opened this for: the
  // reviewer's state is what they took out.
  const [cleared, setCleared] = useState<ReadonlySet<string>>(new Set());
  const selected = new Set(ready.filter((request) => !cleared.has(request.id)).map((request) => request.id));

  const chosen = ready.filter((request) => selected.has(request.id));
  const foods = new Set(chosen.map((request) => request.foodId)).size;

  const add = useMutation({
    mutationFn: () =>
      approveNames(
        api,
        chosen.map((request) => request.id),
      ),
    onSuccess: (result) => {
      setCleared(new Set());
      const refused =
        result.refused.length === 0
          ? ''
          : ` ${plural(result.refused.length, 'was', 'were')} not added: ${result.refused.map((entry) => entry.reason).join(' ')}`;
      onAdded({
        text: `Added ${plural(result.added.length, 'name')}.${refused}`,
        undo: result.added.map((added) => ({ batchId: added.batchId, requestId: added.id })),
      });
    },
    retry: false,
  });

  const toggle = (id: string, on: boolean) => {
    setCleared((current) => {
      const next = new Set(current);
      if (on) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <section aria-labelledby="bulk-title" className="overflow-hidden rounded-xl border border-border bg-card">
      <header className="flex items-center gap-3 px-[18px] pt-3.5 pb-3">
        <h2 id="bulk-title" className="text-[15px] font-semibold text-foreground">
          Names ready to add
        </h2>
        <span className="ml-auto text-xs text-muted-foreground tabular-nums">showing {ready.length}</span>
      </header>

      {add.isError ? (
        <Alert role="alert" tone="err" className="mx-[18px] mb-3 w-auto">
          <AlertDescription>{add.error.message}</AlertDescription>
        </Alert>
      ) : null}

      {list.isPending ? (
        <p className="border-t border-border px-[18px] py-6 text-[13px] text-muted-foreground">Loading…</p>
      ) : ready.length === 0 ? (
        <div className="flex flex-col items-center gap-1 border-t border-border px-[18px] py-7 text-center">
          <span className="font-medium text-foreground">No names waiting</span>
          <span className="max-w-[70ch] text-xs text-muted-foreground">
            {notice?.undo !== undefined && notice.undo.length > 0
              ? notice.undo.length === 1
                ? 'The food answers to its new name in search, typed meals and the coach.'
                : `The ${String(notice.undo.length)} foods answer to their new names in search, typed meals and the coach.`
              : 'Names show here once three people use one and no other food answers to it.'}
          </span>
          <Button variant="outline" size="sm" className="mt-2" onClick={onBack}>
            Back to all requests
          </Button>
        </div>
      ) : (
        <>
          <div
            role="status"
            className="flex flex-wrap items-center gap-x-2.5 gap-y-2 border-t border-border bg-secondary py-2 pr-3 pl-[18px]"
          >
            <span className="font-medium text-foreground">{chosen.length} selected</span>
            <span className="text-xs text-muted-foreground">
              Adds {plural(chosen.length, 'name')} to {plural(foods, 'food')}. No numbers change and no logs
              move.
            </span>
            <span className="ml-auto flex items-center gap-1.5">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setCleared(new Set(ready.map((request) => request.id)));
                }}
              >
                Clear
              </Button>
              <Button
                size="sm"
                disabled={!can.mutate || chosen.length === 0 || add.isPending}
                title={can.mutate ? undefined : ADMIN_ONLY}
                onClick={() => {
                  add.mutate();
                }}
              >
                <CheckIcon aria-hidden="true" className="size-3.5" />
                {add.isPending ? 'Adding…' : `Add ${plural(chosen.length, 'name')}`}
              </Button>
            </span>
          </div>
          <div className="overflow-x-auto">
            <div role="table" aria-label="Name requests selected" className="min-w-[900px]">
              <div role="row" className={cn(GRID, 'h-9 border-t border-border bg-secondary')}>
                <span role="columnheader" className="pl-2.5">
                  <label className="flex size-8 cursor-pointer items-center justify-center">
                    <input
                      type="checkbox"
                      aria-label={
                        chosen.length === ready.length ? 'Clear the selection' : 'Select every name'
                      }
                      checked={chosen.length === ready.length}
                      onChange={(event) => {
                        setCleared(
                          event.target.checked ? new Set() : new Set(ready.map((request) => request.id)),
                        );
                      }}
                      className="size-4 cursor-pointer accent-primary"
                    />
                  </label>
                </span>
                <span role="columnheader" className={HEADER_CELL}>
                  New name
                </span>
                <span role="columnheader" className={HEADER_CELL}>
                  Added to
                </span>
                <span role="columnheader" className={HEADER_CELL}>
                  It already answers to
                </span>
                <span role="columnheader" className={HEADER_CELL}>
                  People
                </span>
                <span role="columnheader" className={cn(HEADER_CELL, 'pr-[18px]')}>
                  Checks
                </span>
              </div>
              {ready.map((request, index) => (
                <BulkRow
                  key={request.id}
                  request={request}
                  aliases={details[index]?.data?.food?.aliases ?? null}
                  source={details[index]?.data?.food ?? null}
                  clashes={details[index]?.data?.clashes.length ?? 0}
                  selected={selected.has(request.id)}
                  onToggle={(on) => {
                    toggle(request.id, on);
                  }}
                  onOpen={() => {
                    onOpen(request.id);
                  }}
                />
              ))}
            </div>
          </div>
        </>
      )}
      <div className="border-t border-border bg-secondary/40 px-[18px] py-3 text-xs text-muted-foreground">
        <span className="block max-w-[70ch]">
          Only name requests with high confidence and no clash can be selected together. New foods and fixes
          always open one at a time.
        </span>
      </div>
    </section>
  );
}

function BulkRow({
  request,
  aliases,
  source,
  clashes,
  selected,
  onToggle,
  onOpen,
}: {
  readonly request: AdminFoodRequest;
  readonly aliases: readonly string[] | null;
  readonly source: { readonly source: keyof typeof SOURCE_NAMES; readonly isVerified: boolean } | null;
  readonly clashes: number;
  readonly selected: boolean;
  readonly onToggle: (on: boolean) => void;
  readonly onOpen: () => void;
}) {
  const shown = (aliases ?? []).slice(0, 3);
  const more = (aliases ?? []).length - shown.length;
  return (
    <div
      role="row"
      className={cn(GRID, 'min-h-14 border-t border-border', selected ? 'bg-secondary/50' : 'bg-card')}
    >
      <span role="cell" className="pl-2.5">
        <label className="flex size-8 cursor-pointer items-center justify-center">
          <input
            type="checkbox"
            aria-label={`Select ${request.name}`}
            checked={selected}
            onChange={(event) => {
              onToggle(event.target.checked);
            }}
            className="size-4 cursor-pointer accent-primary"
          />
        </label>
      </span>
      <span role="cell" className="flex flex-col gap-0.5 px-3 py-2">
        <a
          href={`?open=${request.id}`}
          onClick={(event) => {
            event.preventDefault();
            onOpen();
          }}
          className="inline-flex min-h-6 items-center self-start font-medium text-foreground no-underline hover:text-destructive"
        >
          {request.name.charAt(0).toUpperCase() + request.name.slice(1)}
        </a>
        <span className="text-xs text-muted-foreground">{ORIGIN_LABELS[request.origin]}</span>
      </span>
      <span role="cell" className="flex flex-col gap-0.5 px-3 py-2">
        {request.foodId === null ? (
          <span className="font-medium">{request.foodName ?? 'Its food'}</span>
        ) : (
          <Link
            to="/foods/$foodId"
            params={{ foodId: request.foodId }}
            search={{ tab: 'details' }}
            className="inline-flex min-h-6 items-center self-start font-medium text-foreground no-underline hover:text-destructive"
          >
            {request.foodName ?? 'Its food'}
          </Link>
        )}
        <span className="text-xs text-muted-foreground">
          {source === null ? '' : `${SOURCE_NAMES[source.source]}${source.isVerified ? ', verified' : ''}`}
        </span>
      </span>
      <span role="cell" className="flex flex-wrap gap-1 px-3 py-2">
        {aliases === null ? (
          <span className="text-xs text-muted-foreground">Loading…</span>
        ) : aliases.length === 0 ? (
          <span className="text-xs text-muted-foreground">No other names yet</span>
        ) : (
          <>
            {shown.map((alias) => (
              <span
                key={alias}
                className="rounded-[10px] border border-border bg-background px-1.5 py-0.5 text-xs text-muted-foreground"
              >
                {alias}
              </span>
            ))}
            {more > 0 ? <span className="py-0.5 text-xs text-muted-foreground">+{more}</span> : null}
          </>
        )}
      </span>
      <span role="cell" className="flex flex-col gap-px px-3 py-2">
        <span className="font-mono text-[13px] tabular-nums">{request.userCount}</span>
        <span className="text-xs text-muted-foreground">{request.userCount === 1 ? 'person' : 'people'}</span>
      </span>
      <span
        role="cell"
        className={cn('py-2 pr-[18px] pl-3 text-xs', clashes === 0 ? 'text-ok-fg' : 'text-err-fg')}
      >
        {clashes === 0 ? 'No clash, same food' : 'Another food has this name'}
      </span>
    </div>
  );
}
